<template>
  <div class="row poppins h-100">
    <div class="col-12 h-100">
      <div class="table-wrapper h-100 d-flex flex-column">
        <div class="filter-block">
          <SearchInput :searchChange="searchChange" />
          <b-button variant="primary" size="lg" class="pl-3 pr-4 d-flex align-items-center" @click="goToAddContent">
            <BIconPlus class="mr-2" />
            {{ 'New content' | i18n }}
          </b-button>
        </div>
        <Table
          :update="getPage"
          :columns="columns"
          :rows="rows"
          :filter="filter"
          :updateFilter="updateFilter"
          :loadingInstance="loadingInstance"
          :setLoadingInstance="setLoadingInstance"
          :selectedList="selectedList"
          :setSelectedList="setSelectedList"
          class="overflow-auto pr-1"
          tableName="contentTable"
        />
      </div>
    </div>
  </div>
</template>

<script>
import moment from 'moment';
import { BIconPlus, BIconSearch } from 'bootstrap-vue';
import { DeleteRowMixin, NotificationMixin, AuthMixin } from '../../../../mixins';
import PageHeader from '../../common/page-header';
import Table from '../../common/table';
import SearchInput from '../../common/search-input';
import api from '../../../api';

let inputInterval = null;

export default {
  title: 'Content',
  components: { PageHeader, BIconPlus, BIconSearch, Table, SearchInput },
  mixins: [DeleteRowMixin, NotificationMixin, AuthMixin],
  data() {
    return {
      selectedList: [],
      loadingInstance: null,
      filter: {
        page: 1,
        limit: 10,
        totalCount: 0,
        sortBy: '',
        sortOrder: '',
        searchQuery: '',
      },
      columns: [
        {
          field: 'name',
          title: this.$translate('Name'),
          key: '2',
          sortBy: '',
          align: 'left',
        },
        {
          field: 'description',
          title: this.$translate('description'),
          key: '3',
          sortBy: '',
          align: 'left',
        },
        {
          field: 'createdAt',
          title: this.$translate('Date Created'),
          key: '4',
          sortBy: '',
          align: 'left',
        },
        {
          field: 'actions',
          title: '',
          key: '6',
          width: 50,
          renderBodyCell: ({ row }) => {
            return (
              <div class="action-block">
                <div class="copy-button" on-click={() => this.goToDetails(row.id)}>
                  {this.$translate('Details')}
                </div>
                {this.isCommonAdmin ? (
                  <div class="download-button delete-button" on-click={() => this.deleteContent(row.id)}>
                    {this.$translate('Delete')}
                  </div>
                ) : null}
              </div>
            );
          },
        },
      ],
      rows: [],
    };
  },
  methods: {
    async goToDetails(id) {
      this.$router.push({ name: 'content-details', params: { id } });
    },
    async goToAddContent() {
      this.$router.push({ name: 'create-content' });
    },
    updateFilter(f) {
      this.filter = {
        ...this.filter,
        ...f,
      };
    },
    setSelectedList(content) {
      this.selectedList = content;
    },
    setLoadingInstance(loadingInstance) {
      this.loadingInstance = loadingInstance;
    },
    searchChange(e) {
      const v = e.target.value;
      this.filter.searchQuery = v;
      if (inputInterval) {
        clearTimeout(inputInterval);
      }
      inputInterval = setTimeout(() => {
        this.getPage();
      }, 500);
    },
    startLoading() {
      this.loadingInstance.show();
    },
    stopLoading() {
      this.loadingInstance.close();
    },
    async deleteContent(id) {
      await this.deleteSelectedRows(
        null,
        async () => {
          try {
            this.startLoading();
            await api.content.remove(id);
            this.getPage();
          } catch (err) {
            this.stopLoading();
            this.showNotification({ type: 'error', message: err.message });
          }
        },
        false
      );
    },
    async getPage() {
      try {
        this.startLoading();
        const { page, limit, sortBy, sortOrder, status, dateRange, searchQuery } = this.filter;
        const ctx = {
          page,
          limit,
          sortBy,
          sortOrder,
          searchQuery,
        };
        if (status) {
          ctx.status = status;
        }
        if (dateRange && dateRange.startDate && dateRange.endDate) {
          ctx.startDate = dateRange.startDate;
          ctx.endDate = dateRange.endDate;
        }
        const response = await api.content.get(ctx);
        response.data = response.data.map((item) => ({
          ...item,
          createdAt: moment(item.createdAt).format('DD.MM.YY HH:mm'),
        }));
        this.filter.totalCount = response.totalCount;
        this.stopLoading();
        this.rows = response.data;
      } catch (err) {
        this.showNotification({ type: 'error', message: err.message });
      }
    },
  },
};
</script>

<style scoped lang="scss">
::v-deep .ve-table-body {
  ::v-deep th {
    color: rgb(112, 112, 112) !important;
    background-color: rgb(239, 239, 239) !important;
    border: none !important;
    min-height: 50px;
  }
  ::v-deep td {
    color: rgb(112, 112, 112) !important;
    cursor: pointer;
  }
}

::v-deep .ve-table-cell-selection {
  border: none !important;
}

::v-deep .ve-table-sort {
  top: -4.5px;
}

::v-deep .details-action {
  width: 35px;
  height: 35px;
  border-radius: 50%;
  background: rgb(34, 129, 254);
  display: flex;
  justify-content: center;
  align-items: center;
  color: white;
  font-size: 28px;
  margin: auto;
}

.statuses-block {
  display: flex;
  justify-content: flex-end;
  margin-bottom: 20px;
}

.date-range-block {
  min-width: 350px;
  text-align: center;
  margin-left: 30px;
  display: flex;
  align-items: center;
}

::v-deep .ve-select-selected-span {
  font-weight: 400;
}

.pagination-block {
  display: flex;
  justify-content: flex-end;
  margin-top: 30px;
}

.action-block {
  display: flex;
}

::v-deep .copy-button {
  background: rgb(237, 248, 254);
  border: 2px solid rgb(175, 216, 255);
  border-radius: 4px;
  padding: 2px 13px;
  margin: 0px 5px;
  color: rgb(49, 141, 255);
  display: flex;
  justify-content: center;
  align-items: center;
  align-content: center;
  margin-right: 20px;
  cursor: pointer;
}

::v-deep .delete-button {
  background: rgb(219, 71, 79);
  color: white;
  border-color: #db474f;
  border: 2px solid #db474f;
  border-radius: 4px;
  padding: 2px 13px;
  margin: 0px 5px;
  cursor: pointer;
}

::v-deep .btn-lg {
  font-size: 16px;
  line-height: 24px;
}
</style>
